import React, { useState, useEffect } from 'react';
import { TimeSlotSelector } from './TimeSlotSelector';
import { ToggleSwitch } from './ToggleSwitch';
import { DayScheduleList } from './DayScheduleList';
import { DayCustomizer } from './DayCustomizer';
import {
  isValidTimeSlot as validateTimeSlot,
  isValidTimeSlot,
  isOvernightTime,
  hasTimeOverlap,
  timeToMinutes,
  format12Hour,
  addMinutesToTime,
} from '../../utils/timeValidation';

// -------------------------
// Types
// -------------------------
interface TimeSlot {
  start: string;
  end: string;
}

interface DaySchedule {
  firstHalf: TimeSlot;
  secondHalf: TimeSlot;
  isClosed: boolean;
}

type OperatingHours = Record<
  'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday',
  DaySchedule
>;

interface OperatingHoursGeneratorProps {
  onChange: (hours: OperatingHours) => void;
  initialHours?: OperatingHours;
}

const DAYS: Array<keyof OperatingHours> = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

// -------------------------
// Empty Schedule
// -------------------------
const EMPTY_SCHEDULE: OperatingHours = DAYS.reduce((acc, day) => {
  acc[day] = {
    firstHalf: { start: '', end: '' },
    secondHalf: { start: '', end: '' },
    isClosed: false,
  };
  return acc;
}, {} as OperatingHours);

// -------------------------
// Component
// -------------------------
export const OperatingHoursGenerator: React.FC<OperatingHoursGeneratorProps> = ({
  onChange,
  initialHours,
}) => {
  const [operatingHours, setOperatingHours] = useState<OperatingHours>(
    initialHours || EMPTY_SCHEDULE
  );
  const [defaultTimes, setDefaultTimes] = useState<TimeSlot>({ start: '', end: '' });
  const [breakTimes, setBreakTimes] = useState({
    firstPeriod: { start: '', end: '' },
    secondPeriod: { start: '', end: '' },
  });
  const [scheduleGenerated, setScheduleGenerated] = useState(false);
  const [activePreset, setActivePreset] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [customizedDays, setCustomizedDays] = useState<Set<keyof OperatingHours>>(new Set());
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // ✅ REMOVED: No more alerts when top-level times change
  // Customized days are preserved during regeneration

  // -------------------------
  // Helper Functions
  // -------------------------
  const getPreviousDay = (day: keyof OperatingHours): keyof OperatingHours => {
    const index = DAYS.indexOf(day);
    return DAYS[(index - 1 + 7) % 7];
  };

  // -------------------------
  // Validate All Days (Monday → Sunday)
  // -------------------------
  const validateAllDays = (schedule: OperatingHours): string[] => {
    const errors: string[] = [];
    
    console.log('=== VALIDATING ALL DAYS ===');
    
    DAYS.forEach((day, index) => {
      const daySchedule = schedule[day];
      
      // Skip closed days
      if (daySchedule.isClosed) {
        console.log(`${day}: Closed - skipping validation`);
        return;
      }
      
      const prevDay = getPreviousDay(day);
      const prevSchedule = schedule[prevDay];
      
      console.log(`\nValidating ${day}:`);
      console.log(`  Previous day: ${prevDay}`);
      console.log(`  ${day} starts at: ${daySchedule.firstHalf.start}`);
      
      // Check if previous day's first period extends into current day
      if (prevSchedule.firstHalf.start && prevSchedule.firstHalf.end) {
        if (isOvernightTime(prevSchedule.firstHalf.start, prevSchedule.firstHalf.end)) {
          console.log(`  ${prevDay} first period is overnight, ends at: ${prevSchedule.firstHalf.end}`);
          
          // Use <= to prevent overlap (no touching at boundary)
          if (daySchedule.firstHalf.start && daySchedule.firstHalf.start <= prevSchedule.firstHalf.end) {
            const dayCapitalized = day.charAt(0).toUpperCase() + day.slice(1);
            const prevDayCapitalized = prevDay.charAt(0).toUpperCase() + prevDay.slice(1);
            const error = `${dayCapitalized} cannot start at ${format12Hour(daySchedule.firstHalf.start)} because ${prevDayCapitalized}'s overnight period ends at ${format12Hour(prevSchedule.firstHalf.end)} (${dayCapitalized}). ${dayCapitalized} must start AFTER ${format12Hour(prevSchedule.firstHalf.end)}.`;
            errors.push(error);
            console.log(`  ❌ ERROR: ${error}`);
          } else {
            console.log(`  ✅ No conflict with ${prevDay}'s first period`);
          }
        }
      }
      
      // Check if previous day's second period extends into current day
      if (prevSchedule.secondHalf.start && prevSchedule.secondHalf.end) {
        if (isOvernightTime(prevSchedule.secondHalf.start, prevSchedule.secondHalf.end)) {
          console.log(`  ${prevDay} second period is overnight, ends at: ${prevSchedule.secondHalf.end}`);
          
          // Use <= to prevent overlap (no touching at boundary)
          if (daySchedule.firstHalf.start && daySchedule.firstHalf.start <= prevSchedule.secondHalf.end) {
            const dayCapitalized = day.charAt(0).toUpperCase() + day.slice(1);
            const prevDayCapitalized = prevDay.charAt(0).toUpperCase() + prevDay.slice(1);
            const error = `${dayCapitalized} cannot start at ${format12Hour(daySchedule.firstHalf.start)} because ${prevDayCapitalized}'s second overnight period ends at ${format12Hour(prevSchedule.secondHalf.end)} (${dayCapitalized}). ${dayCapitalized} must start AFTER ${format12Hour(prevSchedule.secondHalf.end)}.`;
            errors.push(error);
            console.log(`  ❌ ERROR: ${error}`);
          } else {
            console.log(`  ✅ No conflict with ${prevDay}'s second period`);
          }
        }
      }
    });
    
    console.log(`\nTotal errors: ${errors.length}`);
    console.log('===========================\n');
    
    return errors;
  };

  // -------------------------
  // Validate default times
  // -------------------------
  const isDefaultTimesValid = (): boolean => {
    if (!defaultTimes.start || !defaultTimes.end) return false;

    // ✅ UPDATE 3: Allow 24-hour case explicitly
    if (defaultTimes.start === defaultTimes.end) {
      return true;
    }

    return validateTimeSlot(defaultTimes.start, defaultTimes.end);
  };

  const getDefaultTimesError = (): string => {
    if (!defaultTimes.start || !defaultTimes.end) return '';
    // No error for same start/end time - it means 24-hour operation
    return '';
  };

  // -------------------------
  // Validate break times
  // -------------------------
  const isBreakTimesValid = (): boolean => {
    if (!isValidTimeSlot(breakTimes.firstPeriod.start, breakTimes.firstPeriod.end)) {
      return false;
    }
    if (!isValidTimeSlot(breakTimes.secondPeriod.start, breakTimes.secondPeriod.end)) {
      return false;
    }
    if (
      hasTimeOverlap(
        breakTimes.firstPeriod.start,
        breakTimes.firstPeriod.end,
        breakTimes.secondPeriod.start,
        breakTimes.secondPeriod.end
      )
    ) {
      return false;
    }
    return true;
  };

  const getBreakTimesError = (): string => {
    if (!isValidTimeSlot(breakTimes.firstPeriod.start, breakTimes.firstPeriod.end)) {
      return 'First period: Start or end time is missing or invalid';
    }
    if (!isValidTimeSlot(breakTimes.secondPeriod.start, breakTimes.secondPeriod.end)) {
      return 'Second period: Start or end time is missing or invalid';
    }
    if (
      hasTimeOverlap(
        breakTimes.firstPeriod.start,
        breakTimes.firstPeriod.end,
        breakTimes.secondPeriod.start,
        breakTimes.secondPeriod.end
      )
    ) {
      return 'Periods overlap - please adjust times';
    }
    return '';
  };

  // -------------------------
  // Generate full schedule
  // -------------------------
  const handleGenerate = () => {
    let newSchedule: OperatingHours;

    if (activePreset === 'breakfastDinner') {
      newSchedule = DAYS.reduce((acc, day) => {
        // Skip customized days
        if (customizedDays.has(day)) {
          acc[day] = operatingHours[day];
          console.log(`Skipping ${day} - already customized`);
        } else {
          acc[day] = {
            firstHalf: { ...breakTimes.firstPeriod },
            secondHalf: { ...breakTimes.secondPeriod },
            isClosed: false,
          };
        }
        return acc;
      }, {} as OperatingHours);
    } else {
      // ✅ UPDATE 4: Block second period if 24-hour operation
      const is24Hour = defaultTimes.start === defaultTimes.end;

      newSchedule = DAYS.reduce((acc, day) => {
        // Skip customized days
        if (customizedDays.has(day)) {
          acc[day] = operatingHours[day];
          console.log(`Skipping ${day} - already customized`);
        } else {
          acc[day] = {
            firstHalf: { ...defaultTimes },
            // Block second period if 24-hour
            secondHalf: is24Hour ? { start: '', end: '' } : { start: '', end: '' },
            isClosed: false,
          };
        }
        return acc;
      }, {} as OperatingHours);
    }

    // Validate the generated schedule
    const errors = validateAllDays(newSchedule);
    setValidationErrors(errors);

    if (errors.length > 0) {
      console.error('Generated schedule has validation errors:', errors);
      // Update schedule for validation but DON'T proceed to day list
      setOperatingHours(newSchedule);
      // DON'T set scheduleGenerated = true - keep customize visible
      // DON'T call onChange - don't commit invalid schedule
      return; // Stop here - user can fix via customize
    }

    // Only proceed if no errors
    setOperatingHours(newSchedule);
    setScheduleGenerated(true);
    onChange(newSchedule);
  };

  // -------------------------
  // Edit schedule (go back to edit mode without clearing)
  // -------------------------
  const handleClear = () => {
    setScheduleGenerated(false);
    // DON'T clear operatingHours - preserve customized days
    // DON'T clear customizedDays - keep track of what was customized
    // DON'T call onChange with empty - keep current schedule
  };

  // -------------------------
  // Toggle preset
  // -------------------------
  const togglePreset = (preset: string) => {
    if (activePreset === preset) {
      // Toggling OFF - clear preset
      setActivePreset(null);
    } else {
      // Toggling ON
      if (preset === 'breakfastDinner') {
        // Copy defaultTimes to breakTimes.firstPeriod to preserve values
        if (defaultTimes.start && defaultTimes.end) {
          setBreakTimes((prev) => ({
            ...prev,
            firstPeriod: { 
              start: defaultTimes.start, 
              end: defaultTimes.end 
            },
          }));
        }
      }
      setActivePreset(preset);
    }
  };

  // -------------------------
  // Update individual day
  // -------------------------
  const handleDayChange = (
    day: keyof OperatingHours,
    field: 'firstHalf' | 'secondHalf' | 'isClosed',
    value: any
  ) => {
    const newSchedule = {
      ...operatingHours,
      [day]: {
        ...operatingHours[day],
        [field]: value,
      },
    };

    // ✅ UPDATE 2: If day closed → clear times (CRITICAL BUG FIX)
    if (field === 'isClosed' && value === true) {
      newSchedule[day].firstHalf = {
        start: '',
        end: '',
      };
      newSchedule[day].secondHalf = {
        start: '',
        end: '',
      };
    }

    setOperatingHours(newSchedule);
    onChange(newSchedule);
  };

  // -------------------------
  // Update single time with auto-correction
  // -------------------------
  const handleTimeChange = (
    day: keyof OperatingHours,
    period: 'firstHalf' | 'secondHalf',
    time: 'start' | 'end',
    value: string
  ) => {
    const draftSchedule = {
      ...operatingHours,
      [day]: {
        ...operatingHours[day],
        [period]: {
          ...operatingHours[day][period],
          [time]: value,
        },
      },
    };

    const currentDayData = draftSchedule[day];
    const firstHalf = currentDayData.firstHalf;
    const secondHalf = currentDayData.secondHalf;

    if (period === 'firstHalf' && time === 'start') {
      if (
        firstHalf.end &&
        !isOvernightTime(firstHalf.start, firstHalf.end) &&
        timeToMinutes(firstHalf.end) <= timeToMinutes(firstHalf.start)
      ) {
        draftSchedule[day].firstHalf.end = '';
      }
      if (
        secondHalf.start &&
        secondHalf.end &&
        isValidTimeSlot(firstHalf.start, firstHalf.end) &&
        isValidTimeSlot(secondHalf.start, secondHalf.end) &&
        hasTimeOverlap(firstHalf.start, firstHalf.end, secondHalf.start, secondHalf.end)
      ) {
        draftSchedule[day].secondHalf.start = '';
        draftSchedule[day].secondHalf.end = '';
      }
    }

    if (period === 'firstHalf' && time === 'end') {
      if (
        secondHalf.start &&
        secondHalf.end &&
        isValidTimeSlot(firstHalf.start, firstHalf.end) &&
        isValidTimeSlot(secondHalf.start, secondHalf.end) &&
        hasTimeOverlap(firstHalf.start, firstHalf.end, secondHalf.start, secondHalf.end)
      ) {
        draftSchedule[day].secondHalf.start = '';
        draftSchedule[day].secondHalf.end = '';
      }
    }

    if (period === 'secondHalf' && time === 'start') {
      if (
        secondHalf.end &&
        !isOvernightTime(secondHalf.start, secondHalf.end) &&
        timeToMinutes(secondHalf.end) <= timeToMinutes(secondHalf.start)
      ) {
        draftSchedule[day].secondHalf.end = '';
      }
    }

    // Clear Second Period if First Period becomes 24-hour operation
    if (period === 'firstHalf') {
      const firstHalf = draftSchedule[day].firstHalf;
      if (firstHalf.start && firstHalf.end && firstHalf.start === firstHalf.end) {
        draftSchedule[day].secondHalf.start = '';
        draftSchedule[day].secondHalf.end = '';
      }
    }

    setOperatingHours(draftSchedule);
    onChange(draftSchedule);
  };

  // -------------------------
  // Render
  // -------------------------
  return (
    <div className="border border-[var(--border-color)] rounded-lg">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-[var(--bg-hover)] transition-colors"
      >
        <div className="flex items-center space-x-3">
          <h4 className="text-base font-semibold text-[var(--text-primary)]">Operating Hours</h4>
          <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-semibold">
            required
          </span>
        </div>
        <svg
          className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 border-t border-[var(--border-color)]">
          {!scheduleGenerated && (
            <div className="mt-4 p-3 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg">
              {activePreset === 'breakfastDinner' && (
                <div className="space-y-3 mb-3">
                  <div>
                    <p className="text-xs font-medium text-[var(--text-primary)] mb-2">First Period</p>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <TimeSlotSelector
                          label="Start"
                          value={breakTimes.firstPeriod.start}
                          onChange={(v) =>
                            setBreakTimes((prev) => ({
                              ...prev,
                              firstPeriod: { ...prev.firstPeriod, start: v },
                            }))
                          }
                          id="top-level-break-first-start"
                        />
                      </div>
                      <div className="flex-1">
                        <TimeSlotSelector
                          label="End"
                          value={breakTimes.firstPeriod.end}
                          onChange={(v) =>
                            setBreakTimes((prev) => ({
                              ...prev,
                              firstPeriod: { ...prev.firstPeriod, end: v },
                            }))
                          }
                          minTime={breakTimes.firstPeriod.start}
                          id="top-level-break-first-end"
                        />
                        {breakTimes.firstPeriod.start && breakTimes.firstPeriod.end && 
                         isOvernightTime(breakTimes.firstPeriod.start, breakTimes.firstPeriod.end) && (
                          <p className="text-xs text-orange-700 mt-1">
                            (Next Day)
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-[var(--text-primary)] mb-2">Second Period</p>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <TimeSlotSelector
                          label="Start"
                          value={breakTimes.secondPeriod.start}
                          onChange={(v) =>
                            setBreakTimes((prev) => ({
                              ...prev,
                              secondPeriod: { ...prev.secondPeriod, start: v },
                            }))
                          }
                          minTime={breakTimes.firstPeriod.end}
                          maxTime={breakTimes.firstPeriod.start}
                          excludeBeforeMin={true}
                          id="top-level-break-second-start"
                        />
                      </div>
                      <div className="flex-1">
                        <TimeSlotSelector
                          label="End"
                          value={breakTimes.secondPeriod.end}
                          onChange={(v) =>
                            setBreakTimes((prev) => ({
                              ...prev,
                              secondPeriod: { ...prev.secondPeriod, end: v },
                            }))
                          }
                          minTime={breakTimes.secondPeriod.start}
                          maxTime={breakTimes.firstPeriod.start}
                          excludeBeforeMin={true}
                          id="top-level-break-second-end"
                        />
                        {breakTimes.secondPeriod.start && breakTimes.secondPeriod.end && 
                         isOvernightTime(breakTimes.secondPeriod.start, breakTimes.secondPeriod.end) && (
                          <p className="text-xs text-orange-700 mt-1">
                            (Next Day)
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {!activePreset && (
                <>
                  <p className="text-xs font-medium text-[var(--text-primary)] mb-2">First Period</p>
                  <div className="flex gap-2 mb-3">
                    <div className="flex-1">
                      <TimeSlotSelector
                        label="Start Time"
                        value={defaultTimes.start}
                        onChange={(v) => setDefaultTimes((prev) => ({ ...prev, start: v }))}
                        id="top-level-default-start"
                      />
                    </div>
                    <div className="flex-1">
                      <TimeSlotSelector
                        label="End Time"
                        value={defaultTimes.end}
                        onChange={(v) => setDefaultTimes((prev) => ({ ...prev, end: v }))}
                        minTime={defaultTimes.start}
                        id="top-level-default-end"
                      />
                      {defaultTimes.start && defaultTimes.end && 
                       isOvernightTime(defaultTimes.start, defaultTimes.end) && (
                        <p className="text-xs text-orange-700 mt-1">
                          (Next Day)
                        </p>
                      )}
                    </div>
                  </div>
                </>
              )}
              
              {!activePreset && getDefaultTimesError() && (
                <p className="text-red-600 text-xs mb-3 flex items-center space-x-1">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>{getDefaultTimesError()}</span>
                </p>
              )}
              {activePreset === 'breakfastDinner' && getBreakTimesError() && (
                <p className="text-red-600 text-xs mb-3 flex items-center space-x-1">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>{getBreakTimesError()}</span>
                </p>
              )}
              
            </div>
          )}

          {!scheduleGenerated && (
            <div className="mt-3 inline-block">
              <ToggleSwitch
                label="Second Operating Period"
                isActive={activePreset === 'breakfastDinner'}
                onClick={() => togglePreset('breakfastDinner')}
              />
            </div>
          )}

          {/* Pluggable DayCustomizer Component - Only visible before generation */}
          {!scheduleGenerated && (
            <div className="mt-4 mb-4">
              <DayCustomizer
                operatingHours={operatingHours}
                onChange={(newHours) => {
                  // Mark ALL changed days as customized (not just the first one)
                  const changedDays = DAYS.filter(day => 
                    JSON.stringify(operatingHours[day]) !== JSON.stringify(newHours[day])
                  );
                  
                  if (changedDays.length > 0) {
                    setCustomizedDays(prev => {
                      const newSet = new Set(prev);
                      changedDays.forEach(day => newSet.add(day));
                      return newSet;
                    });
                    console.log(`Marked as customized:`, changedDays);
                    console.log(`Total customized days:`, Array.from(new Set([...Array.from(customizedDays), ...changedDays])));
                  }
                  
                  // Validate after customize
                  const errors = validateAllDays(newHours);
                  setValidationErrors(errors);
                  
                  setOperatingHours(newHours);
                  onChange(newHours);
                }}
              />
            </div>
          )}

          {/* Display Validation Errors - Moved after DayCustomizer */}
          {validationErrors.length > 0 && !scheduleGenerated && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="space-y-3">
                {validationErrors.map((error, index) => (
                  <div key={index} className="flex items-start space-x-2 pb-3 border-b border-red-200 last:border-0 last:pb-0">
                    <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <p className="text-sm text-red-700 flex-1 leading-relaxed">{error}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!scheduleGenerated && (
            <button
              onClick={handleGenerate}
              disabled={
                (!activePreset && !isDefaultTimesValid()) ||
                (activePreset === 'breakfastDinner' && !isBreakTimesValid())
              }
              className="mt-3 w-full px-3 py-2 bg-[var(--accent-color)] text-white rounded font-medium text-sm hover:bg-[var(--accent-color)]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-1.5"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                  clipRule="evenodd"
                />
              </svg>
              <span>Generate All Days</span>
            </button>
          )}

          {scheduleGenerated && (
            <>
              <div className="flex justify-end mt-4">
                <button
                  onClick={handleClear}
                  className="px-3 py-1.5 bg-white text-gray-700 border border-gray-300 rounded text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  Edit
                </button>
              </div>
              
              <DayScheduleList
                operatingHours={operatingHours}
                onDayChange={handleDayChange}
                onTimeChange={handleTimeChange}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};
